import { useState, useEffect } from "react";
import { apiService } from "../services/api";
import { getUser } from "../services/authAPI";

export type ApiOption = "openai" | "gemini" | "grok" | "llama" | "gemma-12b" | "phi" | "gemma-4b" | "gpt-4o" | "gpt-5";

interface Message {
  id: string;
  content: string;
  sender: "user" | "bot";
  timestamp: Date;
  contexts?: string[];
}

interface ChatResponse {
  success: boolean;
  data: {
    responses: {
      [key in ApiOption]?: {
        answer: string;
        extraQn?: string[];
      };
    };
    sessionId?: string;
    contextsUsed?: string[];
  };
}


interface ChatRequest {
  message?: string;
  selectedApi: ApiOption;
  userId: string | null;
  contexts?: string[];
  history?: string[];
  gender?: string;
  age?: string;
}

export const useChat = (selectedApi: ApiOption = "openai") => {
  const [history, setHistory] = useState<string[]>([]);
  const [extraQuestions, setExtraQuestions] = useState<string[]>([]);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      content:
        "Hello! I'm your personal health assistant. To get started, may I know your age and gender? How can I assist you today?",
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const userId = localStorage.getItem("userId");
        
        if (!userId) return;

        const res = await apiService.get<{ success: boolean; data: string[]; message?: string }>(
          `/chat/history/${userId}`
        );

        if (res.success && res.data) {
          setHistory(res.data);
        }
      } catch (error) {
        console.error("Error fetching history:", error);
      }
    };

    fetchHistory();
  }, []);

  const refreshHistory = async () => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      const res = await apiService.get<{ success: boolean; data: string[]; message?: string }>(
        `/chat/history/${userId}`
      );

      if (res.success && res.data) {
        setHistory(res.data);
      }
    } catch (error) {
      console.error("Error refreshing history:", error);
    }
  };

  const sendMessage = async (content: string, contexts: string[] = [], history: string[] = []) => {
    const userMessage: Message = {
      id: Date.now().toString(),
      content,
      sender: "user",
      timestamp: new Date(),
      contexts: contexts.length > 0 ? [...contexts] : undefined,
    };
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
  
    try {
      const userId: string | null = localStorage.getItem("userId");
      let parsedData = localStorage.getItem("data");
      let gender = "";
      let age = "";
  
      if (parsedData) {
        const data = JSON.parse(parsedData);
        gender = data.gender;
        age = data.age;
      }
  
      
      if (!gender || !age) {
        const genderInput = document.querySelector<HTMLInputElement>("input[name='gender']:checked");
        const ageInput = document.querySelector<HTMLInputElement>("input#age");
  
        gender = genderInput?.value || "";
        age = ageInput?.value || "";
  
      
        if (gender && age) {
          localStorage.setItem("data", JSON.stringify({ gender, age }));
        }
      }
  
      const requestPayload: ChatRequest = {
        message: content || "History",
        userId,
        selectedApi,
        ...(contexts.length > 0 && { contexts }),
        ...(history.length > 0 && { history }),
        ...(age && { age }),
        ...(gender && { gender }),
      };
  
      const data: ChatResponse = await apiService.post<ChatResponse>(
        "/chat/message",
        requestPayload
      );
  
      if (data.success && data.data.responses[selectedApi]) {
        const { answer, extraQn } = data.data.responses[selectedApi]!;
  
        const botMessage: Message = {
          id: Date.now().toString(),
          content: `${answer}`,
          sender: "bot",
          timestamp: new Date(),
        };
  
        setMessages((prev) => [...prev, botMessage]);
  
        if (extraQn && extraQn.length > 0) {
          setExtraQuestions(extraQn);
        } else {
          setExtraQuestions([]);
        }
  
        await refreshHistory();
      } else {
        setMessages((prev) => [...prev, {
          id: Date.now().toString(),
          content: "⚠️ No response from server",
          sender: "bot",
          timestamp: new Date(),
        }]);
      }
  
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) => [...prev, {
        id: Date.now().toString(),
        content: "❌ Error contacting server",
        sender: "bot",
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  
  return {
    history,
    messages, 
    isLoading, 
    sendMessage,
    extraQuestions,
    setExtraQuestions
  };
};

export const useAuth = () => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  // let tem = {
  //   name : "Anbu",
  //   email: "Anbu3.frn"
  // }
  useEffect(() => {
    getUser().then((data) => {
      setUser(data);
      setLoading(false);
    });
  }, []);

  return { user, setUser, loading };
};